import React, { useState, useEffect } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Globe, Menu, X, ChevronDown } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_plastic-wealth/artifacts/guwzj2f5_2.png';

export default function Navbar() {
  const { t, language, setLanguage, LANGUAGES } = useLanguage();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { href: '#about', label: t('nav.about') },
    { href: '#credits', label: t('nav.credits') },
    { href: '#process', label: t('nav.process') },
    { href: '#value', label: t('nav.value') },
    { href: '#benefits', label: t('nav.benefits') },
    { href: '#team', label: t('nav.team') },
  ];

  const scrollTo = (e, href) => {
    e.preventDefault();
    setMobileOpen(false);
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const currentLang = LANGUAGES.find(l => l.code === language);

  return (
    <nav
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'nav-blur py-3' : 'bg-transparent py-5'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 flex items-center justify-between">
        {/* Logo */}
        <a href="#hero" onClick={(e) => scrollTo(e, '#hero')} className="flex items-center gap-3" data-testid="navbar-logo">
          <img src={LOGO_URL} alt="CSR & Finance" className="h-10 w-10" />
          <span className="hidden sm:block font-semibold text-white text-sm tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>
            CSR & Finance Consulting
          </span>
        </a>

        {/* Desktop nav links */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              className="text-sm text-slate-300 hover:text-[#42D2D9] transition-colors duration-200"
              data-testid={`nav-link-${link.href.slice(1)}`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Right: Language + CTA */}
        <div className="flex items-center gap-4">
          {/* Language Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger
              data-testid="language-switcher"
              className="flex items-center gap-1.5 text-sm text-slate-300 hover:text-white transition-colors px-3 py-2 rounded border border-white/10 hover:border-white/20 bg-transparent outline-none"
            >
              <Globe className="w-4 h-4" />
              <span>{currentLang?.label}</span>
              <ChevronDown className="w-3 h-3" />
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-[#0F2A3E] border-white/10 min-w-[140px]">
              {LANGUAGES.map((lang) => (
                <DropdownMenuItem
                  key={lang.code}
                  onClick={() => setLanguage(lang.code)}
                  data-testid={`lang-${lang.code}`}
                  className={`cursor-pointer text-sm ${
                    language === lang.code ? 'text-[#42D2D9]' : 'text-slate-300'
                  } hover:text-white hover:bg-white/5`}
                >
                  <span className="font-semibold mr-2">{lang.label}</span>
                  {lang.name}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* CTA Button */}
          <a
            href="#booking"
            onClick={(e) => scrollTo(e, '#booking')}
            data-testid="nav-cta-booking"
            className="hidden md:inline-flex cta-primary items-center"
          >
            <span>{t('nav.booking')}</span>
          </a>

          {/* Mobile Menu Toggle */}
          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-white"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile nav */}
      {mobileOpen && (
        <div data-testid="mobile-menu" className="lg:hidden nav-blur mt-2 mx-4 rounded-lg p-6 space-y-4">
          {navLinks.map((link) => (
            <a
              key={link.href}
              href={link.href}
              onClick={(e) => scrollTo(e, link.href)}
              className="block text-base text-slate-300 hover:text-[#42D2D9] transition-colors"
            >
              {link.label}
            </a>
          ))}
          <a
            href="#booking"
            onClick={(e) => scrollTo(e, '#booking')}
            className="block cta-primary text-center mt-4"
          >
            <span>{t('nav.booking')}</span>
          </a>
        </div>
      )}
    </nav>
  );
}
